import { Component, OnInit, AfterViewInit, OnDestroy, ElementRef, ViewChild, Input, Output, EventEmitter } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { User } from '@angular/fire/auth';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { CallService, CallData } from '../../services/call.service';

@Component({
  selector: 'app-video-call',
  templateUrl: './video-call.component.html',
  styleUrls: ['./video-call.component.css'],
  standalone: true,
  imports: [
    CommonModule
  ]
})
export class VideoCallComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('localVideo') localVideoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('remoteVideo') remoteVideoRef!: ElementRef<HTMLVideoElement>;

  @Input() receiverUid: string = '';
  @Input() receiverEmail: string = '';
  @Input() isCallActive: boolean = false;
  @Output() callEnded: EventEmitter<void> = new EventEmitter<void>();

  isMuted: boolean = false;
  isVideoOff: boolean = false;
  currentUser: User | null = null;

  // Call state tracking
  callState: {
    isActive: boolean;
    isOutgoing: boolean;
    isIncoming: boolean;
    callerEmail: string;
    receiverId: string;
    status: string;
  } = {
      isActive: false,
      isOutgoing: false,
      isIncoming: false,
      callerEmail: '',
      receiverId: '',
      status: ''
    };

  private peerConnection: RTCPeerConnection | null = null;
  private localStream: MediaStream | null = null;
  public remoteStream: MediaStream | null = null;
  private subscriptions: Subscription[] = [];
  private currentCallData: CallData | null = null;
  private processedIceCandidates = new Set<string>();

  constructor(
    private authService: AuthService,
    private toastr: ToastrService,
    private callService: CallService
  ) { }

  ngOnInit(): void {
    console.log('VideoCallComponent initialized');

    // Subscribe to user authentication state
    this.subscriptions.push(
      this.authService.userSubject.subscribe(user => {
        this.currentUser = user;
        console.log('Current user:', user?.email);
      })
    );

    // Subscribe to call data
    this.subscriptions.push(
      this.callService.callData$.subscribe(callData => {
        if (callData) {
          this.currentCallData = callData;
          this.handleCallData(callData);
        } else {
          this.resetCallState();
        }
      })
    );
  }

  ngAfterViewInit(): void {
    // The view elements are available here.
  }

  ngOnDestroy(): void {
    this.cleanup();

    // Clean up all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  // Handle call data, especially for ended calls
  private handleCallData(callData: CallData): void {
    console.log('Handling call data:', callData);

    // Check if the call has ended or was rejected
    if (callData.status === 'ended' || callData.status === 'rejected') {
      console.log('Call ended or rejected, cleaning up...');
      this.handleCallEnded();
      return;
    }

    // Update call state
    this.callState = {
      isActive: callData.status === 'accepted',
      isOutgoing: this.currentUser?.uid === callData.providerUid,
      isIncoming: this.currentUser?.uid === callData.clientUid && callData.status === 'incoming',
      callerEmail: callData.providerEmail || '',
      receiverId: callData.clientUid || '',
      status: callData.status || ''
    };

    console.log('Updated call state:', this.callState);

    // Setup WebRTC if not already done
    if (!this.peerConnection && (this.callState.isIncoming || this.callState.isOutgoing)) {
      this.setupPeerConnection();
      // Wait for setup to complete before processing further
      return;
    }

    // Process in order: offer -> answer -> candidates
    if (this.peerConnection) {
      this.processWebRTCData(callData);
    }
  }

  private handleCallEnded(): void {
    console.log('Handling call ended');

    // Cleanup local resources first
    this.cleanup();
    this.resetCallState();

    // Emit event for the parent component to handle
    setTimeout(() => {
      this.callEnded.emit();
    }, 100); // Small delay to ensure cleanup is complete
  }

  private async processWebRTCData(callData: CallData): Promise<void> {
    if (!this.peerConnection) return;

    try {
      // 1. Process offer for incoming calls
      if (callData.offer && this.callState.isIncoming && !this.peerConnection.remoteDescription) {
        console.log('Processing offer...');
        await this.peerConnection.setRemoteDescription(new RTCSessionDescription(callData.offer));
      }

      // 2. Process answer for outgoing calls
      if (callData.answer && this.callState.isOutgoing && !this.peerConnection.remoteDescription) {
        console.log('Processing answer...');
        await this.peerConnection.setRemoteDescription(new RTCSessionDescription(callData.answer));
      }

      // 3. Process ICE candidates after remote description is set
      if (callData.candidates && callData.candidates.length > 0 && this.peerConnection.remoteDescription) {
        console.log('Processing ICE candidates...');
        await this.addIceCandidates(callData.candidates);
      }
    } catch (error) {
      console.error('Error processing WebRTC data:', error);
    }
  }

  private resetCallState(): void {
    this.callState = {
      isActive: false,
      isOutgoing: false,
      isIncoming: false,
      callerEmail: '',
      receiverId: '',
      status: ''
    };
    this.processedIceCandidates.clear();

    // Reset UI states
    this.isMuted = false;
    this.isVideoOff = false;
  }
  // Optimized cleanup()
  private cleanup(): void {
    console.log('Cleaning up video call...');

    // Stop all local tracks
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => {
        track.stop();
        console.log('Stopped local track:', track.kind);
      });
      this.localStream = null;
    }

    // Stop all remote tracks
    if (this.remoteStream) {
      this.remoteStream.getTracks().forEach(track => {
        track.stop();
        console.log('Stopped remote track:', track.kind);
      });
      this.remoteStream = null;
    }

    // Close peer connection
    if (this.peerConnection) {
      // Remove event listeners to avoid any race conditions
      this.peerConnection.onicecandidate = null;
      this.peerConnection.ontrack = null;
      this.peerConnection.onnegotiationneeded = null;
      this.peerConnection.onconnectionstatechange = null;
      this.peerConnection.close();
      this.peerConnection = null;
    }

    // Clear video elements
    if (this.localVideoRef?.nativeElement) {
      this.localVideoRef.nativeElement.srcObject = null;
      this.localVideoRef.nativeElement.pause();
    }
    if (this.remoteVideoRef?.nativeElement) {
      this.remoteVideoRef.nativeElement.srcObject = null;
      this.remoteVideoRef.nativeElement.pause();
    }
    this.processedIceCandidates.clear();
    // Reset UI state
    this.isMuted = false;
    this.isVideoOff = false;
  }
  // Modified setupPeerConnection to disable echo and add TURN server
  private async setupPeerConnection(): Promise<void> {
    const configuration = {
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        // {
        //   urls: 'turn:your-turn-server.com:3478',
        //   username: 'your-username',
        //   credential: 'your-password'
        // }
      ]
    };

    this.peerConnection = new RTCPeerConnection(configuration);

    // Handle ICE candidates
    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate && this.currentCallData) {
        console.log('Sending ICE candidate');
        this.callService.updateCallData({
          candidates: [event.candidate.toJSON()]
        }, this.currentCallData.clientUid);
      }
    };

    // Handle remote stream
    this.peerConnection.ontrack = (event) => {
      console.log('Remote track received:', event.track.kind);
      if (event.track) {
        if (!this.remoteStream) {
          this.remoteStream = new MediaStream();
        }
        this.remoteStream.addTrack(event.track);
        this.displayRemoteStream();
      }
    };

    // OPTIMIZATION: Use onnegotiationneeded to create offers reliably
    this.peerConnection.onnegotiationneeded = async () => {
        console.log('Negotiation needed...');
        if (this.callState.isOutgoing) {
            await this.createOffer();
        }
    };

    // Handle connection state changes
    this.peerConnection.onconnectionstatechange = () => {
      console.log('Connection state:', this.peerConnection?.connectionState);
      if (this.peerConnection?.connectionState === 'connected') {
        console.log('WebRTC connection established successfully');
      }
    };

    // Get local media stream with echo cancellation
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      // Add local tracks to peer connection
      this.localStream.getTracks().forEach(track => {
        if (this.peerConnection && this.localStream) {
          console.log('Adding local track:', track.kind);
          this.peerConnection.addTrack(track, this.localStream);
        }
      });

      // Display local video muted to prevent echo
      this.displayLocalStream();

    } catch (error) {
      console.error('Error accessing media devices:', error);
      this.toastr.error('Could not access camera or microphone', 'Error');
      this.endCall();
    }
  }

  private displayLocalStream(): void {
    if (this.localVideoRef && this.localVideoRef.nativeElement && this.localStream) {
      this.localVideoRef.nativeElement.srcObject = this.localStream;
      this.localVideoRef.nativeElement.muted = true;
      this.localVideoRef.nativeElement.play().catch(e => console.log('Local video play error:', e));
    } else {
      // If element is not ready, try again in 100ms
      setTimeout(() => this.displayLocalStream(), 100);
    }
  }

  private displayRemoteStream(): void {
    if (this.remoteVideoRef && this.remoteVideoRef.nativeElement && this.remoteStream) {
      this.remoteVideoRef.nativeElement.srcObject = this.remoteStream;
      this.remoteVideoRef.nativeElement.muted = false; // Remote stream should not be muted
      this.remoteVideoRef.nativeElement.play().catch(e => console.log('Remote video play error:', e));
      console.log('Remote video stream displayed');
    } else {
      // Retry after 100ms if element is not ready
      setTimeout(() => {
        this.displayRemoteStream();
      }, 100);
    }
  }

  private async createOffer(): Promise<void> {
    if (!this.peerConnection || !this.currentCallData) return;
    try {
      // Make sure we are in the correct signaling state
      if (this.peerConnection.signalingState !== 'stable') {
        console.log('Cannot create offer in signaling state:', this.peerConnection.signalingState);
        return;
      }
      
      const offerDescription = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offerDescription);
      console.log('Offer created, sending to clientUid:', this.currentCallData?.clientUid);

      // Send offer to the clientUid's document
      await this.callService.updateCallData({
        offer: offerDescription
      }, this.currentCallData.clientUid);
    } catch (error) {
      console.error('Error creating offer:', error);
      this.toastr.error('Could not create call offer', 'Error');
      this.endCall();
    }
  }

  private async handleAnswer(answer: RTCSessionDescriptionInit): Promise<void> {
    if (!this.peerConnection) return;

    try {
      console.log('Handling answer');
      await this.peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
      console.log('Remote description set from answer');
    } catch (error) {
      console.error('Error handling answer:', error);
    }
  }

  private async addIceCandidates(candidates: RTCIceCandidateInit[]): Promise<void> {
    if (!this.peerConnection) return;

    for (const candidate of candidates) {
      const candidateString = JSON.stringify(candidate);
      if (candidate && candidate.candidate && !this.processedIceCandidates.has(candidateString)) {
        try {
          await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
          this.processedIceCandidates.add(candidateString);
          console.log('ICE candidate added.');
        } catch (error) {
          console.error('Error adding ICE candidate:', error);
        }
      }
    }
  }

  // Answer an incoming call
  public async answerCall(): Promise<void> {
    if (!this.peerConnection || !this.callState.isIncoming) return;

    try {
      // Set remote description from offer
      if (this.currentCallData?.offer && !this.peerConnection.remoteDescription) {
        await this.peerConnection.setRemoteDescription(new RTCSessionDescription(this.currentCallData.offer));
        console.log('Remote description set from offer');
      }

      // Create answer
      const answerDescription = await this.peerConnection.createAnswer();
      await this.peerConnection.setLocalDescription(answerDescription);
      console.log('Answer created and local description set');

      // Update call data with answer and status
      await this.callService.updateCallData({
        answer: answerDescription,
        status: 'accepted'
      });

      this.toastr.success('Call connected', 'Success');
    } catch (error) {
      console.error('Error answering call:', error);
      this.toastr.error('Could not answer call', 'Error');
      this.endCall();
    }
  }

  // Add a method to reject a call
  public async rejectCall(): Promise<void> {
    if (!this.callState.isIncoming || !this.currentCallData) return;

    try {
      await this.callService.updateCallData({
        status: 'rejected'
      }, this.currentCallData.clientUid);

      this.cleanup();
      this.resetCallState();
      this.callEnded.emit();
    } catch (error) {
      console.error('Error rejecting call:', error);
    }
  }

  // Optimized endCall()
  public async endCall(): Promise<void> {
    console.log('Ending call manually...');

    // Update Firestore only if the call hasn't been ended already
    const currentCallData = this.callService.getCurrentCallData();
    if (currentCallData && currentCallData.status !== 'ended') {
      await this.callService.endCall(currentCallData.clientUid);
    }

    // Cleanup local resources
    this.cleanup();
    this.resetCallState();

    // Emit event
    this.callEnded.emit();
  }

  // Toggle mute audio
  public toggleMute(): void {
    if (!this.localStream) return;
    this.localStream.getAudioTracks().forEach(track => {
      track.enabled = !track.enabled;
      this.isMuted = !track.enabled;
    });
  }

  public toggleVideo(): void {
    if (!this.localStream) return;
    this.localStream.getVideoTracks().forEach(track => {
      track.enabled = !track.enabled;
      this.isVideoOff = !track.enabled;
    });
  }

  // Switch camera (if multiple cameras are available)
  public async switchCamera(): Promise<void> {
    if (!this.localStream) return;

    // Get current video track
    const currentVideoTrack = this.localStream.getVideoTracks()[0];
    if (!currentVideoTrack) return;

    // Get all video devices
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter(device => device.kind === 'videoinput');

    if (videoDevices.length <= 1) {
      this.toastr.info('No other camera to switch to', 'Info');
      return;
    }

    // Find current device id
    const currentDeviceId = currentVideoTrack.getSettings().deviceId;

    // Find next device
    const currentIndex = videoDevices.findIndex(device => device.deviceId === currentDeviceId);
    const nextDevice = videoDevices[(currentIndex + 1) % videoDevices.length];
    
    // Stop current video track before getting the new one
    currentVideoTrack.stop();
    this.localStream.removeTrack(currentVideoTrack);

    // Get new video track from the next device
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: nextDevice.deviceId } },
        audio: false // We only want to replace the video track
      });

      const newVideoTrack = newStream.getVideoTracks()[0];
      this.localStream.addTrack(newVideoTrack);

      // Replace track in the peer connection
      const sender = this.peerConnection?.getSenders().find(s => s.track?.kind === 'video');
      if (sender) {
        await sender.replaceTrack(newVideoTrack);
      }
      
      // Update local video element source
      if (this.localVideoRef?.nativeElement) {
        this.localVideoRef.nativeElement.srcObject = this.localStream;
      }

      this.toastr.success('Switched camera', 'Success');
    } catch (error) {
      console.error('Error switching camera:', error);
      this.toastr.error('Could not switch camera', 'Error');
      // Try to add the old track back if switching fails
      this.localStream.addTrack(currentVideoTrack);
    }
  }
}
